import * as React from 'react';

import type WorkSummaryDetailVM from 'ab-viewModels/fieldReport/workSummaryDetails.viewModel';

import { bemElement } from 'af-utils/bem.util';
import { downloadCSV } from 'af-utils/csv.utils';

import WorkSummaryBillingType from 'ab-enums/workSummaryBillingType.enum';

import { Button } from '@acceligentllc/storybook';

import Dropdown from 'af-components/Controls/Dropdown';

import { toCSVData } from './helpers';
import styles from './styles.module.scss';
import WorkSummaryDetailsTable from './WorkSummaryDetailsTable';
import type { WorkSummaryTableSortMeta } from '../Table/values';
import { WORK_SUMMARY_TABLE_SORT_META, WorkSummaryTableSortKey } from '../Table/values';

interface OwnProps {
	workSummaryDetails: WorkSummaryDetailVM[];
	onBillingStatusToggle: (workSummaryDetail: WorkSummaryDetailVM) => Promise<void>;
	onRowHighlight: (groupToHighlight: Nullable<string>, groupKeyToHighlight: Nullable<string>) => void;
	highlightedGroup: Nullable<string>;
	highlightedGroupKey: Nullable<string>;
	isReadOnly: boolean;
	workOrderCode: string;
	fieldReportId: number;
	onBulkStatusUpdate: (billingType: WorkSummaryBillingType, isBillable: boolean) => void;
	workSummaryDetailsByGroupKey: Record<string, WorkSummaryDetailVM[]>;
	pathName: string;
	alternativeWorkSummaryDetails: Set<WorkSummaryDetailVM>;
	allowToMarkAllAsNonBillable: boolean;
	canResetJobWorkSummary: boolean;
	resetJobWorkSummary: (fieldReportId: number) => Promise<void>;
	timeZone: Nullable<string>;
}

type Props = OwnProps;

const workSummaryDetailsOptions = Object.values(WORK_SUMMARY_TABLE_SORT_META);

const WorkSummaryDetails: React.FC<Props> = (props) => {
	const {
		workSummaryDetails,
		onBillingStatusToggle,
		onBulkStatusUpdate,
		onRowHighlight,
		highlightedGroup,
		isReadOnly,
		workOrderCode,
		highlightedGroupKey,
		workSummaryDetailsByGroupKey,
		pathName,
		fieldReportId,
		alternativeWorkSummaryDetails,
		allowToMarkAllAsNonBillable,
		canResetJobWorkSummary,
		resetJobWorkSummary,
		timeZone,
	} = props;

	const [sortBy, setSortBy] = React.useState<WorkSummaryTableSortKey>(WorkSummaryTableSortKey.TYPE);

	const onWorkSummaryDetailsOptionsChange = React.useCallback(({ id }: WorkSummaryTableSortMeta<WorkSummaryTableSortKey>) => {
		setSortBy(id);
	}, []);

	const onExportClick = React.useCallback(() => {
		const csvData = toCSVData(workSummaryDetails, timeZone);
		downloadCSV(csvData, `${workOrderCode}_work_summary_details.csv`);
	}, [workOrderCode, workSummaryDetails, timeZone]);

	const handleResetJobWorkSummary = React.useCallback(async () => {
		if (!canResetJobWorkSummary) {
			return;
		}
		await resetJobWorkSummary(fieldReportId);
	}, [canResetJobWorkSummary, fieldReportId, resetJobWorkSummary]);

	const handleMarkBillableWorkAsBillable = React.useCallback(() => {
		onBulkStatusUpdate(WorkSummaryBillingType.BILLABLE_WORK, true);
	}, [onBulkStatusUpdate]);

	const handleMarkBillableWorkAsNonBillable = React.useCallback(() => {
		onBulkStatusUpdate(WorkSummaryBillingType.BILLABLE_WORK, false);
	}, [onBulkStatusUpdate]);

	const handleMarkMaterialExpenseAsBillable = React.useCallback(() => {
		onBulkStatusUpdate(WorkSummaryBillingType.MATERIAL_COST, true);
	}, [onBulkStatusUpdate]);

	const handleMarkMaterialExpenseAsNonBillable = React.useCallback(() => {
		onBulkStatusUpdate(WorkSummaryBillingType.MATERIAL_COST, false);
	}, [onBulkStatusUpdate]);

	const containerClassName = bemElement('field-report__work-summary', 'details');
	const headerClassName = bemElement(containerClassName, 'header');
	const headerTitleClassName = bemElement(headerClassName, 'title');
	const actionsClassName = bemElement(containerClassName, 'actions');

	const showHint = sortBy !== WorkSummaryTableSortKey.TYPE;

	return (
		<div className={containerClassName}>
			<div className={headerClassName}>
				<span className={headerTitleClassName}>
					Details
				</span>
				<Button
					icon="ActionDownload"
					label="Export"
					onClick={onExportClick}
					style="link"
				/>
				<Dropdown
					containerClassName={bemElement('field-report__work-summary__details__header', 'sort-by')}
					defaultValue={workSummaryDetailsOptions[0]}
					label="Sort By"
					labelKey="label"
					onValueChange={onWorkSummaryDetailsOptionsChange}
					options={workSummaryDetailsOptions}
					withBorder={false}
					withCaret={true}
				/>
				{showHint && (
					<span className={bemElement(headerTitleClassName, 'hint')}>
						To enable three-dot menu use sort by type
					</span>
				)}
			</div>
			<WorkSummaryDetailsTable
				alternativeWorkSummaryDetails={alternativeWorkSummaryDetails}
				fieldReportId={fieldReportId}
				highlightedGroup={highlightedGroup}
				highlightedGroupKey={highlightedGroupKey}
				isReadOnly={isReadOnly}
				onBillingStatusToggle={onBillingStatusToggle}
				onRowHighlight={onRowHighlight}
				pathName={pathName}
				sortBy={sortBy}
				timeZone={timeZone}
				workSummaryDetails={workSummaryDetails}
				workSummaryDetailsByGroupKey={workSummaryDetailsByGroupKey}
			/>
			<div className={actionsClassName}>
				{allowToMarkAllAsNonBillable && (
					<div className={styles.billableStatusOptions}>
						<Button
							label="Mark All as Non-Billable"
							onClick={handleMarkBillableWorkAsNonBillable}
							style="secondary"
						/>
						<Button
							label="Mark All as Billable"
							onClick={handleMarkBillableWorkAsBillable}
							style="secondary"
						/>
						<Button
							label="Mark All as Material Non-Expense"
							onClick={handleMarkMaterialExpenseAsNonBillable}
							style="secondary"
						/>
						<Button
							label="Mark All as Material Expense"
							onClick={handleMarkMaterialExpenseAsBillable}
							style="secondary"
						/>
					</div>
				)}
				{canResetJobWorkSummary && (
					<Button
						customStyleClass={styles.resetJobWorkSummaryButton}
						label="Reset Job Work Summary"
						onClick={handleResetJobWorkSummary}
						style="secondary"
					/>
				)}
			</div>
		</div>
	);
};

export default React.memo(WorkSummaryDetails);
